import { useState } from 'react';
import { CmsComponentSwitch } from 'src/cms/CmsComponentSwitch';
import { ContentfulToolbar } from 'src/components/ContentfulToolbar';
import { createSearchDropdown } from 'src/components/Inputs/SearchBar';
import LegacyScripts from 'src/components/LegacyScripts';
import { ContentfulTemplates, Locales, type TLocale } from 'src/lib/constants';
import { searchBasePath } from 'src/lib/resources-utils';
import { TemplateWrapper } from 'src/templates';

const HubTemplate = ({
  fields,
  events,
  noIndex = false,
  topics = [],
  totalResources = 0,
  cmsTheme = 'web 2.0',
  locale = Locales.EN_US,
  ...props
}: {
  fields?;
  events?;
  noIndex?: boolean;
  topics?;
  totalResources?: number;
  cmsTheme?: string;
  locale?: TLocale;
  allTopicArticles?;
}) => {
  // Dropdown values are pageSlugs from createTopicsArray; 'all topics' is hub-wide search.
  const [searchTopic, setSearchTopic] = useState('all topics');

  // creates an object of all the topics ex. {ach:{topic:'ACH'}, api:{topic:'API'}, 'all topics': {topic:{..}}, banking:{topic:'Banking'}}
  const topicsObj = topics.reduce((obj, e) => {
    if (typeof e === 'object') {
      const key = (e.value || e.label || '').toLowerCase();
      obj = { ...obj, [key]: { topic: e } };
      return obj;
    } else {
      obj = { ...obj, [e.toLowerCase()]: { topic: e } };
      return obj;
    }
  }, {});

  // Arranges the topics in alphabetical order based on the keys of the topicsObj ex. ['ACH',{..},'API','Banking']
  const sortedTopics = Object.keys(topicsObj)
    .sort()
    .map((e) => {
      return topicsObj[e].topic;
    });
  topics = sortedTopics;

  // eslint-disable-next-line no-console
  const handleChange = (search) => {
    search?.selectedOption && setSearchTopic(search.selectedOption);
  };

  const topicsDropdown = createSearchDropdown({
    label: 'Topics',
    options: topics,
    defaultOption: 'All Topics',
  });

  totalResources = props.allTopicArticles?.length || totalResources;
  const resultType = 'resource';
  const search = {
    placeholder: 'Search',
    destination: searchBasePath({
      topic: searchTopic,
      locale,
      withQueryParams: false,
    }),
    resultType,
    onChange: handleChange,
    label: `${totalResources} ${resultType}${
      totalResources !== 1 ? 's' : ''
    } available`, // ex: 2 resources available
    dropdowns: [topicsDropdown],
  };

  return (
    <TemplateWrapper
      meta-title={fields?.metaTitle}
      meta-description={fields?.metaDescription}
      hasFooterCta={false}
      noIndex={fields?.metaNoindex || noIndex}
      className={fields?.hubType === 'Events' ? 'event-hub' : 'hub'}
      cmsTheme={cmsTheme}
      locale={locale}
    >
      <div data-testid={ContentfulTemplates.HUB}>
        <CmsComponentSwitch
          components={fields?.components}
          events={events}
          search={topics.length > 0 && search}
          templatePage={ContentfulTemplates.HUB}
        />
      </div>
      <LegacyScripts />
      <ContentfulToolbar />
    </TemplateWrapper>
  );
};

export default HubTemplate;
